import { useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

interface UseSessionTimeoutProps {
  /**
   * Timeout duration in milliseconds
   * Default: 20 minutes (1200000ms)
   */
  timeoutDuration?: number;
  /**
   * Warning time before logout in milliseconds  
   * Default: 2 minutes (120000ms)
   */
  warningTime?: number;
  /**
   * Callback when session is about to expire
   */
  onWarning?: () => void;
  /**
   * Callback when session times out
   */
  onTimeout?: () => void;
  /**
   * Whether to enable the session timeout
   * Default: true
   */
  enabled?: boolean;
}

interface UseSessionTimeoutReturn {
  /**
   * Manually refresh the session timer
   */
  refreshSession: () => void;
  /**
   * Get remaining time in milliseconds
   */
  getRemainingTime: () => number;
  /**
   * Check if session is active
   */
  isActive: boolean;
}

export const useSessionTimeout = ({
  timeoutDuration = 20 * 60 * 1000, // 20 minutes
  warningTime = 2 * 60 * 1000, // 2 minutes  
  onWarning,
  onTimeout,
  enabled = true,
}: UseSessionTimeoutProps = {}): UseSessionTimeoutReturn => {
  const { instance } = useMsal();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isActiveRef = useRef<boolean>(true);

  // Clear existing timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    isActiveRef.current = false;
    clearTimers();
    
    try {
      // Call custom timeout callback first
      onTimeout?.();
      
      // Logout from MSAL
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Error during session timeout logout:', error);
      // Fallback: redirect to home page to trigger re-authentication
      window.location.href = '/';
    }
  }, [instance, onTimeout, clearTimers]);

  // Handle session warning
  const handleWarning = useCallback(() => {
    onWarning?.();
  }, [onWarning]);

  // Start the timeout timers
  const startTimers = useCallback(() => {
    if (!enabled) return;
    
    clearTimers();
    
    const warningTimeout = timeoutDuration - warningTime;
    
    // Set warning timer
    if (warningTimeout > 0) {
      warningRef.current = setTimeout(handleWarning, warningTimeout);
    }
    
    // Set timeout timer
    timeoutRef.current = setTimeout(handleTimeout, timeoutDuration);
    
    lastActivityRef.current = Date.now();
  }, [enabled, timeoutDuration, warningTime, handleWarning, handleTimeout, clearTimers]);

  // Refresh session (reset timers)
  const refreshSession = useCallback(() => {
    if (!enabled) return;
    isActiveRef.current = true;
    startTimers();
  }, [enabled, startTimers]);

  // Get remaining time
  const getRemainingTime = useCallback((): number => {
    if (!enabled || !isActiveRef.current) return 0;
    
    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = timeoutDuration - elapsed;
    return Math.max(0, remaining);
  }, [enabled, timeoutDuration]);

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (enabled && isActiveRef.current) {
      refreshSession();
    }
  }, [enabled, refreshSession]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add throttling to prevent excessive timer resets
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledHandler = () => {
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // Throttle to max once per second
    };

    events.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
    });

    // Initialize timers
    refreshSession();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledHandler, true);
      });
      clearTimers();
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [enabled, handleActivity, refreshSession, clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    refreshSession,
    getRemainingTime,
    isActive: isActiveRef.current,
  };
};